/**
 * StateBadge — coloured pill that visualises an operation state.
 *
 * Colours are derived from a small mapping table; state names come from the
 * backend, never hardcoded UI-side. Unknown states fall back to a neutral
 * slate pill so this component never crashes if the state machine expands.
 */
const TONE = {
  // remittance
  CREATED:              { bg: 'bg-slate-100',  fg: 'text-slate-700',  ring: 'ring-slate-300' },
  PENDING_PAYMENT:      { bg: 'bg-amber-50',   fg: 'text-amber-700',  ring: 'ring-amber-300' },
  PAYMENT_UPLOADED:     { bg: 'bg-amber-100',  fg: 'text-amber-800',  ring: 'ring-amber-400' },
  PAYMENT_UNDER_REVIEW: { bg: 'bg-yellow-100', fg: 'text-yellow-800', ring: 'ring-yellow-400' },
  PAYMENT_CONFIRMED:    { bg: 'bg-sky-100',    fg: 'text-sky-800',    ring: 'ring-sky-400' },
  ASSIGNED_TO_PROVIDER: { bg: 'bg-indigo-100', fg: 'text-indigo-800', ring: 'ring-indigo-400' },
  PROCESSING:           { bg: 'bg-blue-100',   fg: 'text-blue-800',   ring: 'ring-blue-400' },
  DELIVERED:            { bg: 'bg-emerald-100',fg: 'text-emerald-800',ring: 'ring-emerald-400' },
  FAILED:               { bg: 'bg-rose-100',   fg: 'text-rose-800',   ring: 'ring-rose-400' },
  REJECTED:             { bg: 'bg-red-100',    fg: 'text-red-800',    ring: 'ring-red-400' },
  CANCELLED:            { bg: 'bg-slate-200',  fg: 'text-slate-600',  ring: 'ring-slate-300' },
  // ticket-specific (reusing tones)
  AWAITING_PASSENGERS:    { bg: 'bg-amber-50',   fg: 'text-amber-700',  ring: 'ring-amber-300' },
  PASSENGERS_SUBMITTED:   { bg: 'bg-amber-100',  fg: 'text-amber-800',  ring: 'ring-amber-400' },
  DOCUMENTS_UNDER_REVIEW: { bg: 'bg-yellow-100', fg: 'text-yellow-800', ring: 'ring-yellow-400' },
  AWAITING_PROVIDER:      { bg: 'bg-sky-100',    fg: 'text-sky-800',    ring: 'ring-sky-400' },
  TICKET_PROCESSING:      { bg: 'bg-blue-100',   fg: 'text-blue-800',   ring: 'ring-blue-400' },
  TICKET_ISSUED:          { bg: 'bg-indigo-100', fg: 'text-indigo-800', ring: 'ring-indigo-400' },
  PDF_UPLOADED:           { bg: 'bg-emerald-50', fg: 'text-emerald-700',ring: 'ring-emerald-300' },
  COMPLETED:              { bg: 'bg-emerald-100',fg: 'text-emerald-800',ring: 'ring-emerald-400' },
};

// Spanish labels mapped only at the display layer.
// Backend state identifiers (used in transitions / data-testid / audit) remain in English.
const LABEL_ES = {
  CREATED:                'Creado',
  PENDING_PAYMENT:        'Pago pendiente',
  PAYMENT_UPLOADED:       'Pago cargado',
  PAYMENT_UNDER_REVIEW:   'Pago en revisión',
  PAYMENT_CONFIRMED:      'Pago confirmado',
  ASSIGNED_TO_PROVIDER:   'Asignado a proveedor',
  PROCESSING:             'Procesando',
  DELIVERED:              'Entregado',
  FAILED:                 'Fallido',
  REJECTED:               'Rechazado',
  CANCELLED:              'Cancelado',
  AWAITING_PASSENGERS:    'Esperando pasajeros',
  PASSENGERS_SUBMITTED:   'Pasajeros cargados',
  DOCUMENTS_UNDER_REVIEW: 'Documentos en revisión',
  AWAITING_PROVIDER:      'Esperando proveedor',
  TICKET_PROCESSING:      'Ticket en proceso',
  TICKET_ISSUED:          'Ticket emitido',
  PDF_UPLOADED:           'PDF cargado',
  COMPLETED:              'Completado',
};

export const stateLabelEs = (s) => LABEL_ES[s] || (s ? s.replace(/_/g, ' ').toLowerCase() : '');

export const StateBadge = ({ state, size = 'sm' }) => {
  const t = TONE[state] || { bg: 'bg-slate-100', fg: 'text-slate-700', ring: 'ring-slate-300' };
  const pad = size === 'lg' ? 'px-3 py-1 text-xs' : 'px-2 py-0.5 text-[10px]';
  return (
    <span
      data-testid={`state-badge-${state}`}
      className={`inline-flex items-center gap-1 rounded-full font-semibold uppercase tracking-wide ring-1 ring-inset ${t.bg} ${t.fg} ${t.ring} ${pad}`}
    >
      {stateLabelEs(state)}
    </span>
  );
};

export default StateBadge;
